import { AnimatePresence, motion } from 'framer-motion'

import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import { UnitActivationDetailsOptions } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-details/components'
import { CardContainer, ImageComponent } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonCopy, ButtonRoundSm } from '@apps-common/components/elements/button'
import { INPUT_INFO_SM_NO_BORDER, InputInfoSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { UNIT_STATUS_ACTIVE } from '@apps-common/config/unit-filter'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import { ROUTE_UNIT_MANAGE } from '@apps-radionet-portal/config/private-routes'
import { UnitActivationStatusPill } from '../../../components/UnitActivationStatusPill'

interface IProps {
  loading?: boolean
  activation: IActivationServiceRequestModel | undefined
}

export const UnitActivationDetailsCard: React.FC<IProps> = ({ loading, activation }) => {
  const { navigateForward } = useNavigation()
  const {
    status,
    mCentre,
    assignBureau,
    bureau,
    assignTech,
    tech,
    unitId,
    serialNo,
    imei,
    accountNo,
    useLineSupervision,
    productName,
    productImageUrl,
    unitPlanName,
    archived,
    dateEdited,
    editedBy,
    existingUnitId
  } = activation || {}

  const {
    data: existingUnit,
    isLoading: existingUnitIsLoading,
    isError: existingUnitIsError
  } = useGetUnitGenericDetails({
    config: {
      enabled: !!existingUnitId
    },
    unitId: existingUnitId
  })

  const ArrowIcon = commonIcons.arrowAngled
  const NoValueIcon = commonIcons.dash

  const isLoading = loading || !activation
  const isLoadingExisting = !existingUnit || existingUnitIsLoading || existingUnitIsError

  return (
    <>
      <AnimatePresence>
        <motion.div key='unit-activation-details-card-container' {...motionFadeIn(0.25)} className='grid md:grid-cols-2 gap-[20px]'>
          <div className='md:col-span-2'>
            <div className='pl-2'>
              <UnitActivationDetailsOptions activation={activation} displayMode='desktop' />
            </div>
            <CardContainer bgClasses='relative bg-surface border border-border'>
              <div className='grid sm:grid-cols-2 gap-[20px] sm:gap-[10px]'>
                <InputInfoSM
                  id='status'
                  loading={isLoading}
                  showLabel
                  label='Status'
                  value={
                    <div className='flex'>
                      <UnitActivationStatusPill status={status || 'unknown'} />
                    </div>
                  }
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='statusDescription'
                  loading={isLoading}
                  showLabel
                  label='Description'
                  value={activation?.statusDescription?.split(',')?.map((s, i) => (
                    <div key={`unit-activation-status-description-${i}`}>{s}</div>
                  ))}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='editedAt'
                  loading={isLoading}
                  showLabel
                  label='UpdatedAt'
                  value={<div>{dateEdited ? toLocalTimestampStr(dateEdited) : <commonIcons.dash />}</div>}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='editedBy'
                  loading={isLoading}
                  showLabel
                  label='UpdatedBy'
                  value={<div>{editedBy ? editedBy : <commonIcons.dash />}</div>}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
              </div>
            </CardContainer>
          </div>
          <CardContainer bgClasses='relative bg-surface border border-border'>
            <SectionTitle text='Company' classes='pl-2 text-text' />
            <VertSpacerMd />
            <div className='grid grid-cols-2'>
              <InputInfoSM
                id='monitoringCentre'
                loading={isLoading}
                showLabel
                label='Monitoring centre'
                value={mCentre}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='bureau'
                loading={isLoading}
                showLabel
                label='Bureau'
                value={assignBureau ? bureau : <NoValueIcon className='my-1' />}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='technician'
                loading={isLoading}
                showLabel
                label='Technician'
                value={assignTech ? tech : <NoValueIcon className='my-1' />}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
            </div>
          </CardContainer>
          <CardContainer bgClasses='relative bg-surface border border-border'>
            <div className='flex items-center justify-between gap-[5px]'>
              <SectionTitle text={existingUnitId ? 'Replacement Device' : 'Device'} classes='pl-2 text-text' />
              <ButtonRoundSm
                type='button'
                className='bg-brand text-on-brand'
                disabled={isLoading}
                callback={() =>
                  navigateForward({
                    routeUrl: ROUTE_UNIT_MANAGE.buildPath(UNIT_STATUS_ACTIVE.value, unitId),
                    route: ROUTE_UNIT_MANAGE
                  })
                }
              >
                <ArrowIcon />
              </ButtonRoundSm>
            </div>
            <VertSpacerMd />
            <div className='grid grid-cols-2'>
              <InputInfoSM
                id='product'
                loading={isLoading}
                showLabel
                label='Product'
                value={
                  <div className='flex items-center justify-start gap-[5px]'>
                    <ImageComponent
                      src={productImageUrl}
                      alt={productName || ''}
                      imageClasses='w-[20px]'
                      errorIconClasses='text-[1.2rem]'
                    />
                    {productName}
                  </div>
                }
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='plan'
                loading={isLoading}
                showLabel
                label='Plan'
                value={unitPlanName}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='serialNo'
                loading={isLoading}
                showLabel
                label='Serial number'
                value={<ButtonCopy value={serialNo || ''} showValue />}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='imei'
                loading={isLoading}
                showLabel
                label='IMEI'
                value={<ButtonCopy value={imei || ''} showValue />}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='accountNo'
                loading={isLoading}
                showLabel
                label='CID account'
                value={<ButtonCopy value={accountNo || ''} showValue />}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
              <InputInfoSM
                id='useLineSupervision'
                loading={isLoading}
                showLabel
                label='Line supervision'
                value={useLineSupervision ? 'Enabled' : 'Disabled'}
                hideNoEditIcon
                className={INPUT_INFO_SM_NO_BORDER}
              />
            </div>
          </CardContainer>
          {existingUnitId && (
            <CardContainer bgClasses='relative bg-surface border border-border'>
              <div className='flex items-center justify-between gap-[5px]'>
                <SectionTitle text='Existing Device' classes='pl-2 text-text' />
                <ButtonRoundSm
                  type='button'
                  className='bg-brand text-on-brand'
                  disabled={isLoading || isLoadingExisting}
                  callback={() =>
                    navigateForward({
                      routeUrl: ROUTE_UNIT_MANAGE.buildPath(existingUnit?.status, existingUnit?.unitId),
                      route: ROUTE_UNIT_MANAGE
                    })
                  }
                >
                  <ArrowIcon />
                </ButtonRoundSm>
              </div>
              <VertSpacerMd />
              <div className='grid grid-cols-2'>
                <InputInfoSM
                  id='existingProduct'
                  loading={isLoading || isLoadingExisting}
                  showLabel
                  label='Product'
                  value={
                    <div className='flex items-center justify-start gap-[5px]'>
                      <ImageComponent
                        src={existingUnit?.imageUrl}
                        alt={existingUnit?.product || ''}
                        imageClasses='w-[20px]'
                        errorIconClasses='text-[1.2rem]'
                      />
                      {productName}
                    </div>
                  }
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='existingPlan'
                  loading={isLoading || isLoadingExisting}
                  showLabel
                  label='Plan'
                  value={unitPlanName}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='existingMobileNo'
                  loading={isLoading || isLoadingExisting}
                  showLabel
                  label='Mobile No'
                  value={<ButtonCopy value={existingUnit?.mobileNo || ''} showValue />}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='existingImei'
                  loading={isLoading || isLoadingExisting}
                  showLabel
                  label='IMEI'
                  value={<ButtonCopy value={existingUnit?.imei || ''} showValue />}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
                <InputInfoSM
                  id='existingAccountNo'
                  loading={isLoading || isLoadingExisting}
                  showLabel
                  label='CID account'
                  value={<ButtonCopy value={existingUnit?.accountNo || ''} showValue />}
                  hideNoEditIcon
                  className={INPUT_INFO_SM_NO_BORDER}
                />
              </div>
            </CardContainer>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  )
}
